import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, LogOut, Trash2, Trophy, Crown } from 'lucide-react';
import { getGroupDetail, leaveGroup, deleteGroup } from '../api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGroupDetail(id)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  async function handleCopyCode() {
    try {
      await navigator.clipboard.writeText(data.inviteCode);
      toast.success('Invite code copied!');
    } catch {
      toast.error('Failed to copy');
    }
  }

  async function handleLeave() {
    const ok = await confirm('Leave this group?', { confirmLabel: 'Leave' });
    if (!ok) return;
    try {
      await leaveGroup(id);
      toast.success('Left group');
      navigate('/groups');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to leave group');
    }
  }

  async function handleDelete() {
    const ok = await confirm('Delete this group? This cannot be undone.', { confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      await deleteGroup(id);
      toast.success('Group deleted');
      navigate('/groups');
    } catch {
      toast.error('Failed to delete group');
    }
  }

  if (loading) return <div className="loading"><div className="spinner spinner--lg" /></div>;
  if (!data) return <div className="empty-state"><p>Could not load group.</p></div>;

  const { name, inviteCode, isCreator, system, leaderboard } = data;

  return (
    <div className="fade-in">
      <Link to="/groups" className="back-link"><ArrowLeft size={14} /> Groups</Link>

      <div className="page-header__row" style={{ marginBottom: '24px' }}>
        <div>
          <h1 className="page-header__title">{name.toUpperCase()}</h1>
          <p className="page-header__subtitle">
            <Link to={`/systems/${system._id}`} style={{ color: 'inherit' }}>
              {system.name}
            </Link>
            {system.region && ` · ${system.region}`}
          </p>
        </div>
      </div>

      {/* Invite Code */}
      <div className="group-invite">
        <div className="group-invite__label">INVITE CODE</div>
        <div className="group-invite__code-row">
          <span className="group-invite__code">{inviteCode}</span>
          <button className="btn btn--secondary btn--sm" onClick={handleCopyCode}>
            <Copy size={14} /> Copy
          </button>
        </div>
        <p className="group-invite__hint">Share this code with friends to let them join.</p>
      </div>

      {/* Leaderboard */}
      <h2 className="section-header__title" style={{ marginBottom: '12px' }}>LEADERBOARD</h2>
      <div className="leaderboard">
        {leaderboard.map((member, i) => (
          <div key={member._id} className={`leaderboard__row ${member.isYou ? 'leaderboard__row--you' : ''}`}>
            <div className="leaderboard__rank">
              {i === 0 ? <Crown size={16} className="leaderboard__crown" /> : i + 1}
            </div>
            <div className="leaderboard__avatar">
              {(member.username || member.firstName || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="leaderboard__name">
                {member.username || `${member.firstName} ${member.lastName}`}
                {member.isYou && <span className="leaderboard__you-badge">YOU</span>}
              </div>
              <div className="leaderboard__stats">
                {member.completedTrails}/{member.totalTrails} trails · {member.completedKm}/{member.totalKm} km
              </div>
            </div>
            <div className="leaderboard__pct">
              <div className="leaderboard__pct-value">{member.pct}%</div>
              <div className="progress-bar leaderboard__bar">
                <div className="progress-bar__fill" style={{ width: `${member.pct}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="group-actions">
        {isCreator ? (
          <button className="btn btn--danger" onClick={handleDelete}>
            <Trash2 size={14} /> Delete Group
          </button>
        ) : (
          <button className="btn btn--secondary" onClick={handleLeave}>
            <LogOut size={14} /> Leave Group
          </button>
        )}
      </div>
    </div>
  );
}
